import * as React from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

export interface RouterHistory {
  push: (path: string, state?: any) => void;
  replace: (path: string, state?: any) => void;
  goBack: () => void;
  listen: (callback: (...args: any[]) => void) => () => void;
}

export interface WithRouterProps {
  history: RouterHistory;
  location: ReturnType<typeof useLocation>;
  match: { params: Record<string, string | undefined> };
}

// react-router v6 removed the `withRouter` HOC. This shim recreates the
// subset of the v5 router props (history.push/replace/goBack, match.params,
// location) used across the class components in this codebase, so they can
// keep working without being rewritten as function components.
export function withRouter<P extends object>(Component: React.ComponentType<P>): React.FC<any> {
  return function WithRouterWrapper(props: any) {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    const history: RouterHistory = {
      push: (path: string, state?: any) => navigate(path, { state }),
      replace: (path: string, state?: any) => navigate(path, { replace: true, state }),
      goBack: () => navigate(-1),
      listen: () => () => undefined,
    };

    return (
      <Component
        {...(props as P)}
        history={history}
        location={location}
        match={{ params }}
      />
    );
  };
}

// react-router v6 removed the `<Redirect>` component in favour of `<Navigate>`.
export function Redirect({ to }: { to: string }) {
  return <Navigate to={to} replace={true} />;
}
